import { ADMIN_ENDPOINTS, apiFetch, getAuthHeaders, parseResponse } from "./config.js";
import { notifySessionExpired } from "@/auth/session.js";
import { buildLotSearchQuery, buildProductSearchQuery } from "@/utils/productSearchParams.js";
import { buildOrderSearchQuery } from "@/utils/orderFilters.js";
import { downloadPdfBlob } from "@/utils/downloadPdf.js";

export async function searchAdminProducts(filters, page = 0) {
    const params = buildProductSearchQuery(filters, page);
    const response = await apiFetch(`${ADMIN_ENDPOINTS.PRODUCTS}?${params}`, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminCategories() {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCT_CATEGORIES, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminLotNames() {
    const response = await apiFetch(ADMIN_ENDPOINTS.LOT_NAMES, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function searchAdminLots(filters, page = 0) {
    const params = buildLotSearchQuery(filters, page);
    const response = await apiFetch(`${ADMIN_ENDPOINTS.LOTS}?${params}`, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminLot(lotId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.LOT(lotId), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function createAdminLot(payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.LOTS, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function updateAdminLot(lotId, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.LOT(lotId), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function deleteAdminLot(lotId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.LOT(lotId), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function createProduct(payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCTS, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!data.data && !data.success) {
        throw new Error(data.errors?.[0] || "La requête a échoué");
    }
    return data;
}

export async function updateProduct(id, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCT(id), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function deleteProduct(id) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCT(id), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function publishProduct(id) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PUBLISH(id), {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getProductLots(productId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCT_LOTS(productId), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function createProductLot(productId, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCT_LOTS(productId), {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function updateProductLot(productId, lotId, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCT_LOT(productId, lotId), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function deleteProductLot(productId, lotId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.PRODUCT_LOT(productId, lotId), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function uploadFile(file) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiFetch(ADMIN_ENDPOINTS.UPLOAD_FILE, {
        method: "POST",
        headers: getAuthHeaders(false),
        body: formData
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.errors?.[0] || "Échec du téléversement");
    }
    return data;
}

export async function getAdminOrders(filters = {}) {
    const params = buildOrderSearchQuery(filters);
    const query = params.toString();
    const response = await apiFetch(query ? `${ADMIN_ENDPOINTS.ORDERS}?${query}` : ADMIN_ENDPOINTS.ORDERS, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminOrder(orderId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ORDER(orderId), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function updateAdminOrder(orderId, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ORDER(orderId), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function updateAdminOrderStatus(orderId, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ORDER_STATUS(orderId), {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function deleteAdminOrder(orderId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ORDER(orderId), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function downloadAdminOrderReceipt(orderId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ORDER_RECEIPT(orderId), {
        headers: getAuthHeaders(false)
    });
    if (response.status === 401 && localStorage.getItem("token")) {
        notifySessionExpired();
        throw new Error("Session expirée, veuillez vous reconnecter");
    }
    if (!response.ok) {
        let message = `Erreur ${response.status}`;
        try {
            const data = await response.json();
            message = data.errors?.[0] || message;
        } catch {
            // PDF or empty body
        }
        throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
    const rawName = match?.[1] ? decodeURIComponent(match[1].replace(/"/g, "")) : `recu-${orderId}.pdf`;
    await downloadPdfBlob(blob, rawName);
}

export async function searchAdminCustomers(search) {
    return searchAdminCustomerList({ search, page: 0, size: 20 });
}

export async function searchAdminCustomerList({
    search = "",
    accountStatus = "",
    customerType = "",
    page = 0,
    size = 50
} = {}) {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size)
    });
    if (search?.trim()) {
        params.set("search", search.trim());
    }
    if (accountStatus) {
        params.set("accountStatus", accountStatus);
    }
    if (customerType) {
        params.set("customerType", customerType);
    }
    const response = await apiFetch(`${ADMIN_ENDPOINTS.CUSTOMERS}?${params}`, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminCustomer(username) {
    const response = await apiFetch(ADMIN_ENDPOINTS.CUSTOMER(username), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminCustomerCart(username) {
    const response = await apiFetch(ADMIN_ENDPOINTS.CUSTOMER_CART(username), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminCustomerLikedItems(username) {
    const response = await apiFetch(ADMIN_ENDPOINTS.CUSTOMER_LIKED_ITEMS(username), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function updateAdminCustomer(username, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.CUSTOMER(username), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function setAdminCustomerAccountStatus(username, accountStatus) {
    const response = await apiFetch(ADMIN_ENDPOINTS.CUSTOMER(username), {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ accountStatus })
    });
    return parseResponse(response);
}

export async function getAdminStoreSettings() {
    const response = await apiFetch(ADMIN_ENDPOINTS.STORE_SETTINGS, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function updateAdminStoreSettings(payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.STORE_SETTINGS, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function getAdminUser() {
    const response = await apiFetch(ADMIN_ENDPOINTS.USER, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function updateAdminUser(payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.USER, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function changeAdminPassword(payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.CHANGE_PASSWORD, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function completeAdminWalkthrough() {
    const response = await apiFetch(ADMIN_ENDPOINTS.WALKTHROUGH_COMPLETE, {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function searchAdminCarts({ customer = "", search = "", dateFrom = "", dateTo = "", page = 0, size = 50 } = {}) {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size)
    });
    if (customer) {
        params.set("customer", customer);
    }
    if (search) {
        params.set("search", search);
    }
    if (dateFrom) {
        params.set("dateFrom", dateFrom);
    }
    if (dateTo) {
        params.set("dateTo", dateTo);
    }
    const response = await apiFetch(`${ADMIN_ENDPOINTS.CARTS}?${params}`, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function searchAdminLikedItems({ customer = "", search = "", dateFrom = "", dateTo = "", page = 0, size = 50 } = {}) {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size)
    });
    if (customer) {
        params.set("customer", customer);
    }
    if (search) {
        params.set("search", search);
    }
    if (dateFrom) {
        params.set("dateFrom", dateFrom);
    }
    if (dateTo) {
        params.set("dateTo", dateTo);
    }
    const response = await apiFetch(`${ADMIN_ENDPOINTS.LIKED_ITEMS}?${params}`, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function searchAdminReviews({ search = "", status = "", productId = "", page = 0, size = 20 } = {}) {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size)
    });
    if (search) {
        params.set("search", search);
    }
    if (status) {
        params.set("status", status);
    }
    if (productId) {
        params.set("productId", productId);
    }
    const response = await apiFetch(`${ADMIN_ENDPOINTS.REVIEWS}?${params}`, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function deleteAdminReview(reviewId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.REVIEW(reviewId), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function restoreAdminReview(reviewId) {
    const response = await apiFetch(ADMIN_ENDPOINTS.REVIEW_RESTORE(reviewId), {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function listAdminAdvertisements() {
    const response = await apiFetch(ADMIN_ENDPOINTS.ADVERTISEMENTS, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function createAdminAdvertisement(payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ADVERTISEMENTS, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function updateAdminAdvertisement(id, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ADVERTISEMENT(id), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function publishAdminAdvertisement(id) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ADVERTISEMENT_PUBLISH(id), {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function approveAdminAdvertisement(id) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ADVERTISEMENT_APPROVE(id), {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function hideAdminAdvertisement(id) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ADVERTISEMENT_HIDE(id), {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function listAdminEmployees() {
    const response = await apiFetch(ADMIN_ENDPOINTS.EMPLOYEES, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getAdminEmployee(username) {
    const response = await apiFetch(ADMIN_ENDPOINTS.EMPLOYEE(username), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function createAdminEmployee(payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.EMPLOYEES, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function updateAdminEmployee(username, payload) {
    const response = await apiFetch(ADMIN_ENDPOINTS.EMPLOYEE(username), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function setAdminEmployeeAccountStatus(username, accountStatus) {
    const response = await apiFetch(ADMIN_ENDPOINTS.EMPLOYEE_ACCOUNT_STATUS(username), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ accountStatus })
    });
    return parseResponse(response);
}

export async function deleteAdminAdvertisement(id) {
    const response = await apiFetch(ADMIN_ENDPOINTS.ADVERTISEMENT(id), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}
